import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { ImageWidgetSourceEnum } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import { ImagesEntity } from 'libs/gb-dashboard/shared/data-access-images/src'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'

@Component({
  selector: 'gb-presenter-dialog-image-source',
  imports: [
    CommonModule,
    MatDialogModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    GbPresenterWidgetPlaceholderComponent,
  ],
  templateUrl: './gb-presenter-dialog-image-source.component.html',
  styleUrl: './gb-presenter-dialog-image-source.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterDialogImageSourceComponent {
  private readonly dialogRef = inject(MatDialogRef<GbPresenterDialogImageSourceComponent, ImageWidgetSourceEnum | null>)
  private readonly selectedAuctionFacade = inject(SelectedAuctionFacade)
  private readonly presenterContextService = inject(PresenterContextService)

  private readonly selectedAuction = toSignal(this.selectedAuctionFacade.auction$, {
    initialValue: null,
  })

  readonly imageSources = ImageWidgetSourceEnum

  get isSelectedAuctionLoading(): boolean {
    return !this.presenterContextService.isTemplateMode && !this.selectedAuction()
  }

  get hasBannerImage(): boolean {
    return !!this.selectedBannerImage
  }

  get hasLogoImage(): boolean {
    return !!this.selectedAuctionLogo
  }

  get bannerImageEntity(): ImagesEntity {
    return this.selectedBannerImage || this.getFallbackEntity('')
  }

  get logoImageEntity(): ImagesEntity {
    return this.selectedAuctionLogo || this.getFallbackEntity('')
  }

  selectSource(source: ImageWidgetSourceEnum): void {
    this.dialogRef.close(source)
  }

  private get selectedAuctionLogo(): ImagesEntity | null {
    const logo = this.selectedAuction()?.logo
    if (logo?.image) {
      return logo
    }
    return null
  }

  private get selectedBannerImage(): ImagesEntity | null {
    const banners = this.selectedAuction()?.banners || []
    const banner = banners
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .at(0)?.image
    if (banner?.image) {
      return banner
    }
    return null
  }

  private getFallbackEntity(url: string): ImagesEntity {
    return {
      auction: '',
      uuid: '',
      image: url,
      medium_image: url,
      preload_image: url,
      small_image: url,
      thumbnail_image: url,
    }
  }
}
